import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  ArrowClockwise,
  Briefcase,
  Buildings,
  CalendarPlus,
  ChartBar,
  CreditCard,
  CurrencyInr,
  Database,
  EnvelopeSimple,
  Megaphone,
  PencilSimple,
  Prohibit,
  ShieldCheck,
  ShieldStar,
  Sparkle,
  Trash,
  TrendUp,
  UserPlus,
  Warning,
} from "@phosphor-icons/react";
import PlatformEmailSenderTab from "@/components/PlatformEmailSenderTab";
import PlatformAddonsTab from "@/components/PlatformAddonsTab";
import PaymentSettingsTab from "@/components/PaymentSettingsTab";
import DemoDataTab from "@/components/platform/DemoDataTab";
import PublishModerationTab from "@/components/platform/PublishModerationTab";
import PublicAppUrlCard from "@/components/platform/PublicAppUrlCard";
import PlanPresetsTab from "@/components/platform/PlanPresetsTab";
import AuditLogTab from "@/components/platform/AuditLogTab";
import DatabaseQueryTab from "@/components/platform/DatabaseQueryTab";

/**
 * Platform / super-admin console.
 *
 * Available only when `user.platform_role === "super_admin"`. Regular Owners
 * don't see the sidebar entry at all. Backend also enforces the same gate on
 * every /api/platform/* route as defense in depth.
 */
export default function PlatformAdminPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [presets, setPresets] = useState({});
  const [extendOrgId, setExtendOrgId] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [promoteEmail, setPromoteEmail] = useState("");
  const [promoteOpen, setPromoteOpen] = useState(false);
  // Plan editor state
  const [planOrg, setPlanOrg] = useState(null);
  const [planForm, setPlanForm] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);
  // Super admin roster (loaded lazily when the Super Admins tab renders)
  const [superAdmins, setSuperAdmins] = useState(null);
  const [demotingEmail, setDemotingEmail] = useState(null);
  // Add-on status per workspace — used to badge "Enterprise" rows.
  const [enterpriseOrgs, setEnterpriseOrgs] = useState(new Set());

  const loadSuperAdmins = async () => {
    try {
      const { data } = await api.get("/platform/super-admins");
      setSuperAdmins(data.super_admins || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    try {
      const [a, b, p, addons] = await Promise.all([
        api.get("/platform/orgs"),
        api.get("/platform/mrr"),
        api.get("/platform/presets").catch(() => ({ data: { presets: {} } })),
        api.get("/platform/addons/enterprise-workforce/workspaces").catch(() => ({ data: { workspaces: [] } })),
      ]);
      setRows(a.data.orgs || []);
      setMrr(b.data);
      setPresets(p.data.presets || {});
      setEnterpriseOrgs(new Set(
        (addons.data.workspaces || [])
          .filter(w => w.status === "active")
          .map(w => w.org_id),
      ));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (user && user.platform_role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const extend = async () => {
    if (!extendOrgId || !extendDays) return;
    try {
      const { data } = await api.post(`/platform/orgs/${extendOrgId}/extend-trial`, { days: Number(extendDays) });
      toast.success(`Trial extended to ${data.expires_on}`);
      setExtendOrgId(null); setExtendDays(7);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doSuspend = async (org_id) => {
    if (!window.confirm("Suspend this org? Users will keep their data but be locked out.")) return;
    try { await api.post(`/platform/orgs/${org_id}/suspend`); toast.success("Suspended"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doReactivate = async (org_id) => {
    try { await api.post(`/platform/orgs/${org_id}/reactivate`); toast.success("Reactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const promote = async () => {
    if (!promoteEmail) return;
    try {
      await api.post("/platform/promote", { user_email: promoteEmail });
      toast.success(`Promoted ${promoteEmail} to super_admin`);
      setPromoteEmail(""); setPromoteOpen(false);
      loadSuperAdmins();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const demoteSuperAdmin = async (email) => {
    if (!window.confirm(
      `Remove super-admin privileges from ${email}?\n\n` +
      `They will lose access to the /platform console and to all cross-tenant admin tools. ` +
      `Their workspace-level roles (owner, admin, etc.) are NOT affected.`
    )) return;
    setDemotingEmail(email);
    try {
      await api.post("/platform/demote", { user_email: email });
      toast.success(`${email} is no longer a super-admin`);
      loadSuperAdmins();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDemotingEmail(null); }
  };

  const openPlan = (row) => {
    setPlanOrg(row.org);
    setPlanForm({
      tier_name: row.plan.tier_name || "Trial",
      billing_cycle: row.plan.billing_cycle || "trial",
      monthly_price: row.plan.monthly_price ?? 0,
      student_limit: row.plan.student_limit ?? 10,
      user_limit: row.plan.user_limit ?? 3,
      branch_limit: row.plan.branch_limit ?? 1,
      storage_gb_limit: row.plan.storage_gb_limit ?? 1,
      expires_on: row.plan.expires_on || "",
      features: row.plan.features || [],
      notes: row.plan.notes || "",
    });
  };

  const applyPreset = (key) => {
    const p = presets[key];
    if (!p) return;
    setPlanForm(f => ({ ...f, ...p }));
    toast(`Preset "${p.tier_name}" applied — remember to save`, { icon: "✨" });
  };

  const savePlan = async () => {
    if (!planOrg) return;
    setPlanSaving(true);
    try {
      await api.put(`/platform/orgs/${planOrg.id}/plan`, {
        ...planForm,
        student_limit: Number(planForm.student_limit),
        user_limit: Number(planForm.user_limit),
        branch_limit: Number(planForm.branch_limit),
        storage_gb_limit: Number(planForm.storage_gb_limit),
        monthly_price: Number(planForm.monthly_price),
      });
      toast.success("Plan saved");
      setPlanOrg(null); setPlanForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPlanSaving(false); }
  };

  const deleteOrg = async (org_id, name, is_orphan) => {
    const label = is_orphan ? "orphan org (never finished signup)" : "org";
    if (!window.confirm(`Delete this ${label} '${name}' permanently? All tenant data will be wiped. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/platform/orgs/${org_id}`);
      toast.success(`Deleted (${Object.keys(data.deleted || {}).length} collections cleaned)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="space-y-6" data-testid="platform-admin-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline mb-1 flex items-center gap-1.5">
            <ShieldStar size={11} weight="duotone" /> Platform Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Tenants & Subscriptions</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Cross-tenant console. Set subscription tiers, extend trials, suspend accounts, and monitor MRR.
            Regular org Owners never see this — plan changes are their checkout flow.
          </p>
        </div>
        <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="promote-btn">
              <UserPlus size={14} weight="duotone" /> Add super-admin
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md bg-card">
            <DialogHeader><DialogTitle>Promote user to super-admin</DialogTitle></DialogHeader>
            <div className="space-y-1.5">
              <Label>User email</Label>
              <Input value={promoteEmail} onChange={e => setPromoteEmail(e.target.value)} placeholder="ops@emergent.co" data-testid="promote-email" />
              <p className="text-[11px] text-muted-foreground">
                They&apos;ll be able to edit plans, extend trials, and view every tenant&apos;s data.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPromoteOpen(false)}>Cancel</Button>
              <Button onClick={promote} data-testid="promote-confirm">Promote</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="overline flex items-center gap-1.5"><CurrencyInr size={11} weight="duotone" /> Monthly MRR</div>
          <div className="font-display text-2xl font-bold tracking-tight mt-1">{mrr ? inr(mrr.monthly_mrr) : "—"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="overline flex items-center gap-1.5"><TrendUp size={11} weight="duotone" /> Annual ARR</div>
          <div className="font-display text-2xl font-bold tracking-tight mt-1">{mrr ? inr(mrr.annual_arr) : "—"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="overline flex items-center gap-1.5"><Buildings size={11} weight="duotone" /> Active tenants</div>
          <div className="font-display text-2xl font-bold tracking-tight mt-1">{mrr?.total_active_orgs ?? "—"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="overline">Trial tenants</div>
          <div className="font-display text-2xl font-bold tracking-tight mt-1">{mrr?.trial_orgs ?? "—"}</div>
        </CardContent></Card>
      </div>

      {/* Tabs: Tenants + Super Admins */}
      <Tabs defaultValue="tenants" onValueChange={(v) => { if (v === "super_admins" && superAdmins === null) loadSuperAdmins(); }}>
        <div className="max-w-full overflow-x-auto pb-1" data-testid="platform-tabs-scroll">
        <TabsList data-testid="platform-tabs">
          <TabsTrigger value="tenants" data-testid="tab-tenants">
            <Buildings size={14} weight="duotone" className="mr-1.5" /> Tenants
          </TabsTrigger>
          <TabsTrigger value="super_admins" data-testid="tab-super-admins">
            <ShieldCheck size={14} weight="duotone" className="mr-1.5" /> Super Admins
          </TabsTrigger>
          <TabsTrigger value="email_sender" data-testid="tab-email-sender">
            <EnvelopeSimple size={14} weight="duotone" className="mr-1.5" /> Email Sender
          </TabsTrigger>
          <TabsTrigger value="addons" data-testid="tab-addons">
            <Briefcase size={14} weight="duotone" className="mr-1.5" /> Add-ons
          </TabsTrigger>
          <TabsTrigger value="payment_settings" data-testid="tab-payment-settings">
            <CreditCard size={14} weight="duotone" className="mr-1.5" /> Payment Settings
          </TabsTrigger>
          <TabsTrigger value="demo_data" data-testid="tab-demo-data">
            <Database size={14} weight="duotone" className="mr-1.5" /> Demo Data
          </TabsTrigger>
          <TabsTrigger value="publish_moderation" data-testid="tab-publish-moderation">
            <Megaphone size={14} weight="duotone" className="mr-1.5" /> Publish
          </TabsTrigger>
          <TabsTrigger value="plan_presets" data-testid="tab-plan-presets">
            <Sparkle size={14} weight="duotone" className="mr-1.5" /> Plan Presets
          </TabsTrigger>
          <TabsTrigger value="audit_log" data-testid="tab-audit-log">
            <ShieldCheck size={14} weight="duotone" className="mr-1.5" /> Audit Log
          </TabsTrigger>
          <TabsTrigger value="database_query" data-testid="tab-database-query">
            <ChartBar size={14} weight="duotone" className="mr-1.5" /> Database Query
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="tenants" className="mt-4">
          {/* Tenants table */}
          <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
            <Buildings size={16} weight="duotone" /> Tenants ({rows?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Org</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Cycle</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Students</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead className="text-right">Storage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rows || []).map(r => {
                  const suspended = r.org.active === false;
                  const isOrphan = r.is_orphan;
                  const cycle = r.plan.billing_cycle;
                  const isManualGrant = !r.has_subscription && (cycle === "monthly" || cycle === "annual");
                  return (
                    <TableRow key={r.org.id} data-testid={`platform-org-row-${r.org.slug || r.org.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{r.org.name}</span>
                          {isOrphan && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] gap-0.5">
                              <Warning size={10} weight="duotone" /> Orphan
                            </Badge>
                          )}
                          {enterpriseOrgs.has(r.org.id) && (
                            <Badge
                              className="bg-primary/15 text-primary border-primary/30 text-[10px] gap-0.5"
                              title="Enterprise Workforce Suite active"
                              data-testid={`platform-org-enterprise-${r.org.slug}`}
                            >
                              <Briefcase size={10} weight="duotone" /> Enterprise
                            </Badge>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">{r.org.owner_email}</div>
                      </TableCell>
                      <TableCell>{r.plan.tier_name || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className="text-[10px]">{cycle || "—"}</Badge>
                          {isManualGrant && (
                            <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]" title="No Razorpay subscription — plan is manually set by super_admin.">
                              Manual grant
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.plan.monthly_price ? inr(r.plan.monthly_price) : "—"}</TableCell>
                      <TableCell className="text-xs">{r.plan.expires_on || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.usage.students}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.usage.users}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.usage.storage_gb} GB</TableCell>
                      <TableCell>
                        {suspended
                          ? <Badge className="bg-red-100 text-red-800 border-red-200">Suspended</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          {!isOrphan && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openPlan(r)} className="gap-1 h-7 px-2 text-xs" data-testid={`edit-plan-${r.org.id}`} title="Edit plan">
                                <PencilSimple size={12} /> Plan
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setExtendOrgId(r.org.id)} className="gap-1 h-7 px-2 text-xs" data-testid={`extend-${r.org.id}`} title="Extend trial">
                                <CalendarPlus size={12} /> Extend
                              </Button>
                              {suspended ? (
                                <Button size="sm" variant="ghost" onClick={() => doReactivate(r.org.id)} className="gap-1 h-7 px-2 text-xs text-emerald-700" data-testid={`reactivate-${r.org.id}`}>
                                  <ArrowClockwise size={12} /> Reactivate
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" onClick={() => doSuspend(r.org.id)} className="gap-1 h-7 px-2 text-xs text-red-700" data-testid={`suspend-${r.org.id}`}>
                                  <Prohibit size={12} /> Suspend
                                </Button>
                              )}
                            </>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => deleteOrg(r.org.id, r.org.name, isOrphan)} className="gap-1 h-7 px-2 text-xs text-red-700" data-testid={`delete-${r.org.id}`} title="Delete org">
                            <Trash size={12} /> Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="super_admins" className="mt-4">
          <Card data-testid="super-admins-card">
            <CardHeader>
              <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
                <ShieldCheck size={16} weight="duotone" /> Super Admins ({superAdmins?.length ?? 0})
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Users with platform-level access to /platform and cross-tenant admin actions.
                Use <span className="font-medium text-foreground">Add super-admin</span> above to promote,
                or click Remove below to revoke. The last remaining super-admin cannot be removed.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {superAdmins === null ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
              ) : superAdmins.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No super admins yet.</div>
              ) : (
                <div className="divide-y divide-border">
                  {superAdmins.map(sa => {
                    const isSelf = (user?.email || "").toLowerCase() === sa.email;
                    const isLast = superAdmins.length <= 1;
                    const blocked = isSelf || isLast;
                    const blockReason = isSelf && isLast
                      ? "You are the last super-admin — promote another user first, then ask them to remove you."
                      : isSelf
                        ? "You cannot remove yourself — ask another super-admin."
                        : isLast
                          ? "Cannot remove the last super-admin. Promote another user first."
                          : "";
                    return (
                      <div
                        key={sa.email}
                        className="px-5 py-3 flex items-center gap-3"
                        data-testid={`super-admin-row-${sa.email}`}
                      >
                        <Avatar className="h-9 w-9 shrink-0">
                          <AvatarFallback className="bg-primary/15 text-primary font-semibold text-xs">
                            {(sa.name || sa.email || "?").trim().charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-1.5">
                            {sa.name || sa.email}
                            {isSelf && <Badge variant="outline" className="text-[10px]">You</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{sa.email}</div>
                          <div className="text-[10px] text-muted-foreground/80 truncate mt-0.5">
                            {sa.workspaces?.length
                              ? `Workspaces: ${sa.workspaces.map(w => w.org_name || w.org_slug || "unnamed").join(", ")}`
                              : "No workspace membership"}
                          </div>
                        </div>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => demoteSuperAdmin(sa.email)}
                          disabled={blocked || demotingEmail === sa.email}
                          title={blockReason}
                          className="text-xs text-red-700 hover:bg-red-50 gap-1"
                          data-testid={`remove-super-admin-${sa.email}`}
                        >
                          <Trash size={13} /> Remove
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email_sender" className="mt-4">
          <PublicAppUrlCard />
          <PlatformEmailSenderTab />
        </TabsContent>

        <TabsContent value="addons" className="mt-4">
          <PlatformAddonsTab />
        </TabsContent>

        <TabsContent value="payment_settings" className="mt-4">
          <PaymentSettingsTab />
        </TabsContent>

        <TabsContent value="demo_data" className="mt-4">
          <DemoDataTab />
        </TabsContent>

        <TabsContent value="publish_moderation" className="mt-4">
          <PublishModerationTab />
        </TabsContent>

        <TabsContent value="plan_presets" className="mt-4">
          <PlanPresetsTab />
        </TabsContent>

        <TabsContent value="audit_log" className="mt-4">
          <AuditLogTab />
        </TabsContent>

        <TabsContent value="database_query" className="mt-4">
          <DatabaseQueryTab />
        </TabsContent>
      </Tabs>

      {/* Extend trial dialog */}
      <Dialog open={!!extendOrgId} onOpenChange={(o) => !o && setExtendOrgId(null)}>
        <DialogContent className="sm:max-w-sm bg-card">
          <DialogHeader><DialogTitle>Extend trial</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Extend by (days)</Label>
            <Input
              type="number" min={1} max={365}
              value={extendDays}
              onChange={e => setExtendDays(e.target.value)}
              data-testid="extend-days-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Adds days to the current <code>expires_on</code>. If already expired, extends from today.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendOrgId(null)}>Cancel</Button>
            <Button onClick={extend} data-testid="extend-confirm">Extend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan editor dialog (super_admin only) */}
      <Dialog open={!!planOrg} onOpenChange={(o) => { if (!o) { setPlanOrg(null); setPlanForm(null); } }}>
        <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="plan-editor-dialog">
          {planOrg && planForm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  Edit plan — {planOrg.name}
                  <Badge variant="outline" className="text-[10px]">{planOrg.slug}</Badge>
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Direct plan overrides bypass the Razorpay checkout flow. Prefer Extend-trial or letting Razorpay webhooks do the work — this dialog is for grants and remediation.
                </p>
              </DialogHeader>

              <div className="space-y-4">
                {Object.keys(presets).length > 0 && (
                  <div className="flex flex-wrap gap-2 items-center" data-testid="plan-presets">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Apply preset:</span>
                    {Object.entries(presets).map(([key, p]) => (
                      <Button key={key} size="sm" variant="outline" onClick={() => applyPreset(key)} className="gap-1 text-xs" data-testid={`preset-${key}`}>
                        <Sparkle size={12} weight="duotone" /> {p.tier_name}
                      </Button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Tier name</Label>
                    <Input value={planForm.tier_name} onChange={e => setPlanForm(f => ({ ...f, tier_name: e.target.value }))} data-testid="plan-tier-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Billing cycle</Label>
                    <Select value={planForm.billing_cycle} onValueChange={v => setPlanForm(f => ({ ...f, billing_cycle: v }))}>
                      <SelectTrigger data-testid="plan-cycle"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card">
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="one_time">One-time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Student limit</Label>
                    <Input type="number" value={planForm.student_limit} onChange={e => setPlanForm(f => ({ ...f, student_limit: e.target.value }))} data-testid="plan-student-limit" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>User limit</Label>
                    <Input type="number" value={planForm.user_limit} onChange={e => setPlanForm(f => ({ ...f, user_limit: e.target.value }))} data-testid="plan-user-limit" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Branch limit</Label>
                    <Input type="number" value={planForm.branch_limit} onChange={e => setPlanForm(f => ({ ...f, branch_limit: e.target.value }))} data-testid="plan-branch-limit" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Storage limit (GB)</Label>
                    <Input type="number" step="0.1" value={planForm.storage_gb_limit} onChange={e => setPlanForm(f => ({ ...f, storage_gb_limit: e.target.value }))} data-testid="plan-storage-limit" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Monthly price (₹)</Label>
                    <Input type="number" value={planForm.monthly_price} onChange={e => setPlanForm(f => ({ ...f, monthly_price: e.target.value }))} data-testid="plan-price" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Expires on</Label>
                    <Input type="date" value={planForm.expires_on || ""} onChange={e => setPlanForm(f => ({ ...f, expires_on: e.target.value }))} data-testid="plan-expires-on" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea rows={2} value={planForm.notes || ""} onChange={e => setPlanForm(f => ({ ...f, notes: e.target.value }))} placeholder="Grant reason, ticket #, etc." data-testid="plan-notes" />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setPlanOrg(null); setPlanForm(null); }}>Cancel</Button>
                <Button onClick={savePlan} disabled={planSaving} data-testid="save-plan">
                  {planSaving ? "Saving…" : "Save plan"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
